/**
 * Careers content. Positions are static data, not database rows, so a listing
 * can be opened, closed or edited without touching the applications store.
 */

export interface Position {
  slug: string
  title: string
  team: string
  location: string
  type: 'internship' | 'full-time' | 'contract'
  /** One line for the listing card. */
  summary: string
  description: string
  responsibilities: string[]
  requirements: string[]
  bonus: string[]
  isOpen: boolean
}

export const INTERNSHIP_TERMS = {
  location: 'Kigali, Rwanda',
  duration: '3 to 6 months',
  fullTimeNote: 'with the possibility of a full time offer',
  reporting:
    'Each intern reports to a named senior team member and carries a defined set of deliverables from week one.',
} as const

export const POSITIONS: Position[] = [
  {
    slug: 'software-engineering-intern',
    title: 'Software Engineering Intern',
    team: 'Product & Engineering',
    location: INTERNSHIP_TERMS.location,
    type: 'internship',
    summary: 'Build Cavalla Gov across the full stack, and ship features real government users rely on.',
    description:
      'You will help build Cavalla Gov, the platform African governments use to track whether their national development plans are actually being delivered. The platform runs on Next.js, TypeScript, NestJS, and PostgreSQL. You will work across the full stack: building UI components, writing API endpoints, handling data models, and shipping features that real government users rely on.',
    responsibilities: [
      'Build and maintain front end components using React, TypeScript, Tailwind CSS, and shadcn/ui',
      'Write and test REST API endpoints in NestJS with Prisma ORM',
      'Implement role based access control and row level security in PostgreSQL',
      'Write database migrations and seed scripts',
      'Participate in code reviews and ship to production regularly',
      'Collaborate with the data and AI team on integrating analytics features',
    ],
    requirements: [
      'Comfortable with TypeScript, React, and at least one backend framework (Node.js, NestJS, Express, or similar)',
      'Familiar with relational databases (PostgreSQL preferred)',
      'Able to work independently, ask good questions, and write clean, readable code',
    ],
    bonus: [
      'Experience with Next.js App Router, Prisma, or Tailwind CSS',
      'Interest in government technology or public sector digital services',
    ],
    isOpen: true,
  },
  {
    slug: 'data-science-ai-intern',
    title: 'Data Science & AI Intern',
    team: 'Data & Intelligence',
    location: INTERNSHIP_TERMS.location,
    type: 'internship',
    summary: 'Build the funding matching engine and the AI Reporting layer behind Cavalla Gov.',
    description:
      'Cavalla Gov includes a Development Finance Intelligence module that matches government priorities to available funding, and an AI Reporting engine that turns raw progress data into readable briefs for ministers and development partners. You will work on both: building data pipelines, training matching models, and developing the prompts and logic behind the AI reporting layer.',
    responsibilities: [
      'Build and refine the matching engine that connects government programmes to development partner funding opportunities',
      'Design and test prompt chains for the AI Reporting module (Anthropic Claude API)',
      'Clean, transform, and analyse development finance datasets (ODA flows, disbursement records, project databases)',
      'Build dashboards and visualisations for internal analysis and client facing reporting',
      'Evaluate model outputs for accuracy, bias, and usefulness in a policy context',
      'Document data sources, assumptions, and methodologies clearly',
    ],
    requirements: [
      'Strong Python skills (pandas, scikit-learn, or similar)',
      'Experience with data cleaning, analysis, and visualisation',
      'Interest in large language models and prompt engineering',
      'Comfortable working with messy, real world data',
    ],
    bonus: [
      'Experience with development finance data (OECD DAC, IATI, World Bank)',
      'Familiarity with the Anthropic API or other LLM APIs',
    ],
    isOpen: true,
  },
  {
    slug: 'market-research-intern',
    title: 'Market Research & Government Intelligence Intern',
    team: 'Advisory & Research',
    location: INTERNSHIP_TERMS.location,
    type: 'internship',
    summary: 'Research development finance and policy, and write briefs that go to clients.',
    description:
      'Cavalla advises governments and development organisations on where money is going, what is working, and what is not. You will research development finance flows, map donor landscapes, analyse policy documents, and produce briefs that inform real decisions. This is not academic research for a filing cabinet; your work goes to clients.',
    responsibilities: [
      'Research and map development partner activity by country, sector, and instrument (grants, loans, guarantees, results based finance)',
      'Analyse government budget and expenditure data to identify delivery gaps',
      'Monitor policy announcements, donor strategy shifts, and funding opportunities across priority markets',
      'Draft country and sector intelligence briefs for advisory clients',
      "Maintain and update Cavalla's internal knowledge base and research library",
      'Support proposal writing with evidence, data, and case studies',
    ],
    requirements: [
      'Strong research and analytical writing skills',
      'Familiar with development finance concepts (ODA, blended finance, DFIs, multilateral funding)',
      'Able to find, verify, and synthesise information from multiple sources quickly',
      'Comfortable with spreadsheets and basic data analysis',
    ],
    bonus: [
      'Experience working with or studying African public policy, governance, or development',
      'French language skills (several of our target markets are francophone)',
    ],
    isOpen: true,
  },
  {
    slug: 'marketing-communications-intern',
    title: 'Marketing & Communications Intern',
    team: 'Growth & Communications',
    location: INTERNSHIP_TERMS.location,
    type: 'internship',
    summary: 'Shape how Cavalla talks about itself, and grow our audience among government and donors.',
    description:
      'Cavalla is building its public presence from the ground up. You will help shape how we talk about ourselves, what content we put into the world, and how we reach the people who need to know about us. That means writing, designing, planning campaigns, and managing our digital channels, with direct input into brand strategy.',
    responsibilities: [
      'Write and edit content for the website, blog, social media, and email campaigns',
      "Plan and produce materials for Cavalla's webinar programme and public events",
      'Manage social media channels (LinkedIn primarily) and grow our audience among government, donor, and development audiences',
      'Design basic marketing collateral (presentations, one pagers, social graphics) using Canva or similar tools',
      'Track and report on content performance and engagement metrics',
      'Support the preparation of pitch decks and capability statements',
    ],
    requirements: [
      'Strong writing skills with an eye for clear, plain language',
      'Experience managing at least one social media channel for a brand or organisation',
      'Comfortable creating basic visual content (Canva, Figma, or similar)',
      'Interest in development, public policy, or technology for government',
    ],
    bonus: [
      'Experience with email marketing platforms (HubSpot, Mailchimp, or similar)',
      'Video editing or podcast production skills',
    ],
    isOpen: true,
  },
  {
    slug: 'business-development-intern',
    title: 'Business Development & Partnerships Intern',
    team: 'Strategy & Partnerships',
    location: INTERNSHIP_TERMS.location,
    type: 'internship',
    summary: 'Find and pursue government contracts, development partner mandates and partnerships.',
    description:
      'Cavalla grows by winning government contracts, development partner mandates, and strategic partnerships. You will help find and pursue those opportunities: scanning tenders, researching potential clients, drafting proposals, and supporting relationship management with development partners, DFIs, and government agencies.',
    responsibilities: [
      'Monitor procurement portals and development partner pipelines for relevant opportunities (tenders, EOIs, RFPs)',
      'Research prospective government and institutional clients across target markets in Africa',
      'Draft sections of proposals, concept notes, and expressions of interest',
      'Maintain the CRM and track the status of leads, proposals, and partnerships',
      'Support the preparation of meetings, presentations, and follow up materials for client and partner engagements',
      "Map development partner strategies and funding windows relevant to Cavalla's service lines",
    ],
    requirements: [
      'Strong written communication and attention to detail',
      'Interest in business development, partnerships, or institutional sales',
      'Comfortable reading and summarising procurement documents and donor strategy papers',
      'Organised and able to manage multiple deadlines at once',
    ],
    bonus: [
      'Experience with government procurement or development partner bidding processes',
      'Familiarity with CRM tools (HubSpot preferred)',
    ],
    isOpen: true,
  },
]

export function getPosition(slug: string): Position | undefined {
  return POSITIONS.find((position) => position.slug === slug)
}

export const OPEN_POSITIONS = POSITIONS.filter((position) => position.isOpen)

export const WHY_JOIN = [
  {
    title: 'You will do real work.',
    body: 'Interns at Cavalla are not observers. You will own deliverables, sit in on client calls, and see your work go live. If you write a feature, it ships. If you draft a brief, a minister’s office reads it.',
  },
  {
    title: 'You will learn fast.',
    body: 'A small team means you work directly with the founders and senior staff. No layers, no waiting for someone to delegate to you.',
  },
  {
    title: 'You will matter.',
    body: 'The problems we work on affect how millions of people experience public services. That is not a tagline; it is the job.',
  },
  {
    title: 'You will be looked after.',
    body: 'We take care of each other here. Flexible hours, a safe and supportive environment, and a team that genuinely wants you to succeed. We are building a company people are happy to come to work at every day.',
  },
]

export const AVAILABILITY_OPTIONS = [
  { value: 'full-time', label: 'Full time' },
  { value: 'part-time', label: 'Part time' },
  { value: 'flexible', label: 'Flexible' },
] as const

export const CAREER_REFERRAL_SOURCES = [
  'LinkedIn',
  'Twitter/X',
  'Referral',
  'University',
  'Job board',
  'Other',
] as const

export const UPLOAD_LIMITS = {
  maxBytes: 5 * 1024 * 1024,
  extensions: ['.pdf', '.docx'],
  mimeTypes: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
} as const

export const COVER_LETTER_LIMITS = { min: 200, max: 3000 } as const
